import { ArModal } from '@dora/vivian';

export type Closed = { tag: 'modal_closed' };
export type ArLoading = { tag: 'ar_modal_loading'; triggered: boolean };
export type DelayArLoading = { tag: 'ar_delay_loading' };
export type QrLoading = { tag: 'qr_modal_loading' };
export type Qr = { tag: 'qr_modal'; url: string };
export type Ar = { tag: 'ar_modal'; launch: { text: string; action: () => void } };

/**
 * Every possible state the AR Modal can be in.
 *
 * The flow of state is implemented via a reducer and some useEffect hooks in `useAr.ts`.
 *
 * The expected flow of states depends on user interaction and the platform the user is on, but the following is standard:
 *
 * ===DESKTOP===
 *
 * Closed -> QrLoading -> Qr -> Closed
 *
 * ===MOBILE===
 *
 * Closed -> ArLoading -> (opt) DelayArLoading -> Ar -> Closed
 *
 * DelayArLoading would trigger ArLoading again, and the cycle continues until we can go from ArLoading -> Ar
 */
export type ArState =
    /** The AR modal is closed */
    | Closed
    /**
     * The AR model is loading. What this involves is dependent on the platform.
     *
     * iOS generates a GLTF blob direct from the 3D scene, then converts it into USDZ before loading it into modelViewer.
     *
     * Android generates a JSON request describing the models in the 3D scene, and uses the model generation service (frankenstein) to create a GLTF blob URL.
     *
     * Entering this state triggers this process (see useAr.ts)
     */
    | ArLoading
    /**
     * Triggered when AR model loading was unsuccessful, allows for us to try again after a delay.
     * Usually, the reason for a failure is that the 3D Scene has not loaded the models correctly yet.
     */
    | DelayArLoading
    /**
     * QR link is being generated. Currently this only really affects the Configuration
     * journey as we need to make some calls to services to generate a URL. However,
     * this may be true for inspiration in the near future.
     */
    | QrLoading
    /** AR model is loaded and the modal should allow launching into it */
    | Ar
    /** When unable to display AR on the machine, display a QR Code instead */
    | Qr;

export const isClosed = (arState: ArState): arState is Closed => arState.tag === 'modal_closed';
export const isOpen = (arState: ArState): boolean => !isClosed(arState);

export const isQrMode = (arState: ArState): arState is Qr => arState.tag === 'qr_modal';

export const isArLoading = (arState: ArState): arState is ArLoading =>
    arState.tag === 'ar_modal_loading';

export const isArDelayed = (arState: ArState): arState is DelayArLoading =>
    arState.tag === 'ar_delay_loading';

export const isQrLoading = (arState: ArState): arState is QrLoading =>
    arState.tag === 'qr_modal_loading';

export const isArLoaded = (arState: ArState): arState is Ar => arState.tag === 'ar_modal';

// =========================
// The following methods are used to convert the ArState into props for the
// ArModal component. This is largely done via the `toArModalProps` function.
// =========================

const toQrProps = (arState: Qr): ArModal.QrProps => ({
    mode: 'qr',
    loading: false,
    qrCode: arState.url,
});

const toQrLoadingProps = (_: QrLoading): ArModal.QrProps => ({
    mode: 'qr',
    loading: true,
    qrCode: '',
});

const toArProps = (arState: Ar | ArLoading | DelayArLoading): ArModal.ArProps =>
    isArLoaded(arState)
        ? {
              mode: 'ar',
              launchText: arState.launch.text,
              onLaunchClick: arState.launch.action,
              loading: false,
          }
        : { mode: 'ar', launchText: '', onLaunchClick: () => {}, loading: true };

export const toArModalProps = (arState: ArState): ArModal.ArProps | ArModal.QrProps =>
    isClosed(arState)
        ? { mode: 'qr', qrCode: '', loading: true }
        : isQrMode(arState)
        ? toQrProps(arState)
        : isQrLoading(arState)
        ? toQrLoadingProps(arState)
        : toArProps(arState);
